import { when } from '@execonline-inc/maybe-adapter';
import { AlreadyTranslated, NotTranslated } from '@execonline-inc/translations';
import clsx from 'clsx';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import ToggleButton from '../../../../../../../../ToggleButton';
import { PublicNameProgramFamilyShareUrl } from '../../../../../../../Types';
import InviteParticipantStore from '../../../../InviteParticipantModal/Store';
import CopyButton from './CopyButton';
import InviteButton from './InviteButton';
import ShareUrlStore from './ShareUrlStore';
import ShareUrlReactions from './ShareUrlStore/Reactions';
import ProductType from '../../../../../../CurrentCollectionStep/Builder/Experiences/CustomizeRow/ProductType';
import { emptyFragment } from '../../../../../../../../../EmptyFragment';
import { TranslationsContext, translation } from '../../../../../../../../../Translations';
import Image from '../../../../../../../../Image';

interface Props {
  programFamilyShareUrl: PublicNameProgramFamilyShareUrl;
  inviteParticipantStore: InviteParticipantStore;
}

function whenShareUrlIsActiveIn(store: ShareUrlStore): Maybe<{}> {
  return when(store.state.shareUrlResource.payload.active, {});
}

function handleToggle(store: ShareUrlStore) {
  return whenShareUrlIsActiveIn(store)
    .do(() => store.deactivating())
    .elseDo(() => store.activating());
}

function Body({ programFamilyShareUrl, inviteParticipantStore }: Props) {
  const store = useMemo(
    () => new ShareUrlStore(programFamilyShareUrl.shareUrl),
    [programFamilyShareUrl.shareUrl],
  );
  const { links } = store.state.shareUrlResource;
  return (
    <>
      <tr className="bg-white border-b-1 border-gray-300">
        <td className={'text-teal-600 p-5 text-left font-bold w-2/6'}>
          <AlreadyTranslated content={programFamilyShareUrl.name} />
        </td>
        <td className={'p-5'}>
          {<ProductType experienceResource={programFamilyShareUrl.experience} />}
        </td>
        <td className={'p-4 w-1/12'}>
          {programFamilyShareUrl.experience.payload.schoolPartnerLogoUrl
            .map((url) => (
              <TranslationsContext.Consumer>
                {(ts) => (
                  <Image
                    src={url}
                    alt={translation('University partner logo')(ts)}
                    style={'max-h-11'}
                  />
                )}
              </TranslationsContext.Consumer>
            ))
            .getOrElse(emptyFragment)}
        </td>
        <td className={'p-5 text-sm'}>
          <NotTranslated text={programFamilyShareUrl.shareUrl.payload.deliveryChannel} />
        </td>
        <td className="m-0 w-full flex p-5 justify-start">
          <InviteButton
            programFamilyShareUrl={programFamilyShareUrl}
            inviteParticipantStore={inviteParticipantStore}
          />
          <span
            className={clsx({
              'opacity-20 pointer-events-none': whenShareUrlIsActiveIn(store).isNothing(),
            })}
          >
            <CopyButton links={links} />
          </span>
        </td>
        <td className="p-5">
          <ToggleButton
            value={whenShareUrlIsActiveIn(store).isJust()}
            handleOnClick={() => handleToggle(store)}
            ariaLabel={`Toggle access for ${programFamilyShareUrl.name}`}
          />
        </td>
      </tr>
      <ShareUrlReactions store={store} fireImmediately />
    </>
  );
}

export default observer(Body);
